// src/components/packages/PackageList.tsx

import React, { useEffect, useState } from 'react';
import { usePackages } from '../../hooks/usePackages';
import { useAuthContext } from '../../context/AuthContext';
import { PackageResponse, PackageStatus, PACKAGE_STATUS_LABELS, PackageSearchCriteria } from '../../types/package.types';
import { StatusBadge } from '../ui/StatusBadge';
import { RoleGuard } from '../ui/RoleGuard';
import { PackageDetail } from './PackageDetail';
import { PackageForm } from './PackageForm';

const STATUSES = Object.keys(PACKAGE_STATUS_LABELS) as PackageStatus[];

export const PackageList: React.FC = () => {
  const {
    packages, selected, isLoading, error,
    search, getById, create, updateStatus, addLocation, deletePackage, clearError, clearSelected,
  } = usePackages();

  const [showForm, setShowForm]     = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<PackageStatus | ''>('');
  const [formError, setFormError]   = useState<string | null>(null);
  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => { search(); }, []);  // eslint-disable-line

  const handleSearch = () => {
    const criteria: PackageSearchCriteria = {};
    if (searchTerm)   criteria.recipientEmail = searchTerm;
    if (statusFilter) criteria.status         = statusFilter as PackageStatus;
    search(criteria);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSearch();
  };

  const handleCreate = async (data: any) => {
    setFormLoading(true);
    setFormError(null);
    const result = await create(data);
    setFormLoading(false);
    if (result) { setShowForm(false); search(); }
    else setFormError(error);
  };

  const handleRowClick = (pkg: PackageResponse) => getById(pkg.id);

  const fmt = (iso: string) =>
    new Date(iso).toLocaleDateString('es-CO', { dateStyle: 'short' });

  const items = packages?.content ?? [];

  return (
    <div className="pkg-list-page">
      {/* ── Header ── */}
      <div className="pkg-list-page__header">
        <div>
          <h1 className="pkg-list-page__title">Paquetes</h1>
          <p className="pkg-list-page__subtitle">
            {packages ? `${packages.totalElements} paquetes en total` : 'Cargando...'}
          </p>
        </div>
        <RoleGuard roles={['ADMIN', 'OPERATOR']}>
          <button
            className="lgt-btn lgt-btn--primary"
            onClick={() => { setShowForm(true); clearSelected(); }}
          >
            + Nuevo paquete
          </button>
        </RoleGuard>
      </div>

      {/* ── Filters ── */}
      <div className="pkg-filters">
        <input
          className="pkg-input pkg-filters__search"
          placeholder="Buscar por correo..."
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          onKeyDown={handleKeyDown}
        />
        <select
          className="pkg-input pkg-filters__select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as PackageStatus | '')}
        >
          <option value="">Todos los estados</option>
          {STATUSES.map(s => (
            <option key={s} value={s}>{PACKAGE_STATUS_LABELS[s]}</option>
          ))}
        </select>
        <button className="lgt-btn lgt-btn--outline" onClick={handleSearch} disabled={isLoading}>
          Buscar
        </button>
        <button className="lgt-btn lgt-btn--ghost" onClick={() => { setSearchTerm(''); setStatusFilter(''); search(); }}>
          Limpiar
        </button>
      </div>

      {/* ── Global error ── */}
      {error && !showForm && (
        <div className="lgt-alert lgt-alert--error" role="alert">
          <span className="lgt-alert__icon">⚠</span>
          <span className="lgt-alert__text">{error}</span>
          <button type="button" className="lgt-alert__close" onClick={clearError}>×</button>
        </div>
      )}

      {/* ── Main content ── */}
      <div className={`pkg-workspace${selected || showForm ? ' pkg-workspace--split' : ''}`}>

        {/* Table */}
        <div className="pkg-table-wrapper">
          {isLoading && !items.length ? (
            <div className="pkg-table__loading">
              <span className="lgt-btn__spinner" style={{ width: 24, height: 24, borderWidth: 3 }} />
              <p>Cargando paquetes...</p>
            </div>
          ) : items.length === 0 ? (
            <div className="pkg-table__empty">
              <p>No se encontraron paquetes.</p>
            </div>
          ) : (
            <table className="pkg-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Destinatario</th>
                  <th>Ciudad</th>
                  <th>Estado</th>
                  <th>Peso</th>
                  <th>Creado</th>
                </tr>
              </thead>
              <tbody>
                {items.map(pkg => (
                  <tr
                    key={pkg.id}
                    className={`pkg-table__row${selected?.id === pkg.id ? ' pkg-table__row--active' : ''}`}
                    onClick={() => handleRowClick(pkg)}
                  >
                    <td className="pkg-table__id">#{pkg.id.slice(-8)}</td>
                    <td>
                      <div className="pkg-table__name">{pkg.recipient.name}</div>
                      <div className="pkg-table__email">{pkg.recipient.email}</div>
                    </td>
                    <td>{pkg.recipient.address.city}</td>
                    <td><StatusBadge status={pkg.status} /></td>
                    <td>{pkg.weight} kg</td>
                    <td>{fmt(pkg.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {packages && packages.totalPages > 1 && (
            <div className="pkg-pagination">
              {Array.from({ length: packages.totalPages }).map((_, i) => (
                <button
                  key={i}
                  className={`pkg-pagination__btn${packages.number === i ? ' pkg-pagination__btn--active' : ''}`}
                  onClick={() => search({}, i)}
                >
                  {i + 1}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detail panel */}
        {selected && !showForm && (
          <PackageDetail
            pkg={selected}
            onClose={clearSelected}
            onUpdateStatus={updateStatus}
            onAddLocation={addLocation}
            onDelete={deletePackage}
            isLoading={isLoading}
          />
        )}

        {/* Create form panel */}
        {showForm && (
          <div className="pkg-detail">
            <div className="pkg-detail__header">
              <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>Nuevo paquete</h2>
              <button className="pkg-detail__close" onClick={() => setShowForm(false)}>×</button>
            </div>
            <div className="pkg-detail__body">
              <PackageForm
                onSubmit={handleCreate}
                onCancel={() => setShowForm(false)}
                isLoading={formLoading}
                error={formError}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
